<div class="sbd" [ngStyle]="themeVars">
  @if (warnings().length > 0) {
    <div class="sbd-alert sbd-alert--warning" role="status">
      <strong>Some figures may be incomplete.</strong>
      <ul>
        @for (w of warnings(); track w.code + w.detail) {
          <li>{{ w.detail || w.code }}</li>
        }
      </ul>
    </div>
  }

  <!-- Top row: daily chart (3/4) + metric cards stacked on the right (1/4) -->
  <div class="sbd-top">
    <section class="sbd-panel sbd-panel--chart sbd-enter" aria-labelledby="sbd-trends-title">
      <!-- Title, then legend + controls (view, resource, period), then the divider -->
      <div class="sbd-panel__header sbd-panel__header--stacked">
        <div class="sbd-panel__title-row">
          <h3 class="sbd-panel__title" id="sbd-trends-title">Daily session hours</h3>
          @if (isPartial()) {
            <span class="sbd-badge sbd-badge--warning" title="Some data sources were unavailable">Partial data</span>
          }
        </div>

        <div class="sbd-toolbar">
          <div class="sbd-legend" aria-hidden="true">
            @if (showCpu()) {
              <span class="sbd-legend__item"><span class="sbd-legend__dot sbd-legend__dot--cpu"></span>CPU</span>
            }
            @if (showGpu()) {
              <span class="sbd-legend__item"><span class="sbd-legend__dot sbd-legend__dot--gpu"></span>GPU</span>
            }
          </div>

          <div class="sbd-toolbar__controls">
            <div class="sbd-icon-toggle" role="group" aria-label="Chart view">
              @for (opt of chartModeOptions; track opt.value) {
                <button
                  type="button"
                  class="sbd-icon-toggle__btn"
                  [class.sbd-icon-toggle__btn--active]="chartMode() === opt.value"
                  [attr.aria-pressed]="chartMode() === opt.value"
                  [attr.aria-label]="opt.label"
                  [title]="opt.label"
                  (click)="setChartMode(opt.value)"
                >
                  @if (opt.value === 'area') {
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M3 19 L8 11 L12 14 L17 6 L21 10 L21 19 Z" fill="currentColor" opacity="0.25"></path>
                      <path d="M3 19 L8 11 L12 14 L17 6 L21 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                  } @else {
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <rect x="4" y="11" width="4" height="9" rx="1"></rect>
                      <rect x="10" y="5" width="4" height="15" rx="1"></rect>
                      <rect x="16" y="8" width="4" height="12" rx="1"></rect>
                    </svg>
                  }
                </button>
              }
            </div>

            <lib-sandbox-dashboard-dropdown
              ariaLabel="Resource type"
              align="right"
              [multiple]="true"
              [minSelected]="1"
              [options]="resourceOptions"
              [values]="selectedResources()"
              (valuesChange)="setResources($event)"
            ></lib-sandbox-dashboard-dropdown>

            <lib-sandbox-dashboard-dropdown
              prefix="Period:"
              ariaLabel="Period"
              align="right"
              [options]="periodOptions"
              [value]="periodDays()"
              (valueChange)="setPeriod($event)"
            ></lib-sandbox-dashboard-dropdown>
          </div>
        </div>
      </div>

      <div class="sbd-chart" [class.sbd-is-refreshing]="loading().trends && trends()">
        @if (errors().trends; as message) {
          <div class="sbd-empty">
            <span>{{ message }}</span>
            <button type="button" class="sbd-link-btn" (click)="retry('trends')">Retry</button>
          </div>
        } @else if (loading().trends && !trends()) {
          <span class="sbd-skeleton sbd-skeleton--chart"></span>
        } @else if (trends() && trendPoints() === null) {
          <div class="sbd-empty">Daily hours are unavailable for this period.</div>
        } @else if (chartOptions()) {
          <div echarts [options]="chartOptions()!" class="sbd-chart__canvas" role="img" aria-label="Daily session hours chart"></div>
        } @else {
          <div class="sbd-empty">No recorded sessions in this period.</div>
        }
      </div>
    </section>

    @if (errors().summary; as message) {
      <div class="sbd-alert sbd-alert--danger sbd-top__side" role="alert">
        <span>{{ message }}</span>
        <button type="button" class="sbd-link-btn" (click)="retry('summary')">Retry</button>
      </div>
    } @else {
      <section class="sbd-cards sbd-top__side" [class.sbd-is-refreshing]="loading().summary && summary()" aria-label="Overview">
        @for (card of cards(); track card.title; let i = $index) {
          <article class="sbd-card sbd-enter" [style.--sbd-i]="i + 1">
            <div class="sbd-card__header">
              <h3 class="sbd-card__label">{{ card.title }}</h3>
              <span class="sbd-card__caption">{{ card.caption }}</span>
            </div>
            <div class="sbd-card__metrics">
              @for (m of card.metrics; track m.tone) {
                <div
                  class="sbd-metric"
                  [class.sbd-metric--disabled]="m.disabled"
                  [attr.aria-disabled]="m.disabled || null"
                  [title]="m.disabled ? 'Select ' + m.label + ' in the resource filter to see this figure' : ''"
                >
                  <span class="sbd-metric__label">
                    <span class="sbd-legend__dot" [class.sbd-legend__dot--cpu]="m.tone === 'cpu'" [class.sbd-legend__dot--gpu]="m.tone === 'gpu'" aria-hidden="true"></span>
                    {{ m.label }}
                  </span>
                  @if (loading().summary && !summary() && !m.disabled) {
                    <span class="sbd-skeleton sbd-skeleton--value"></span>
                  } @else {
                    <span class="sbd-metric__value" [class.sbd-metric__value--gpu]="m.tone === 'gpu'">{{ m.value }}</span>
                  }
                </div>
              }
            </div>
          </article>
        }

        <!-- Current usage: label / value rows -->
        <article class="sbd-card sbd-card--usage sbd-enter" [style.--sbd-i]="3" aria-label="Current usage">
          <div class="sbd-card__header">
            <h3 class="sbd-card__label">Current usage</h3>
            <span class="sbd-card__caption">Now</span>
          </div>
          @if (loading().summary && !summary()) {
            @for (i of [1, 2, 3, 4]; track i) {
              <span class="sbd-skeleton sbd-skeleton--line"></span>
            }
          } @else {
            <dl class="sbd-usage">
              @for (row of usageRows(); track row.label) {
                <div
                  class="sbd-usage__row"
                  [class.sbd-usage__row--disabled]="row.disabled"
                  [attr.aria-disabled]="row.disabled || null"
                >
                  <dt>{{ row.label }}</dt>
                  <dd>{{ row.value }}</dd>
                </div>
              }
            </dl>
            <div class="sbd-usage__states" aria-label="Sandboxes by state">
              @for (s of strip().states; track s.state) {
                <span class="sbd-usage__state">
                  <span class="sbd-dot" [ngClass]="'sbd-dot--' + stateTone(s.state)" aria-hidden="true"></span>
                  {{ s.count }} {{ s.state }}
                </span>
              } @empty {
                <span class="sbd-usage__state">No sandboxes</span>
              }
            </div>
          }
        </article>
      </section>
    }
  </div>

  <!-- Usage by user -->
  <section class="sbd-panel sbd-panel--flush sbd-enter" [style.--sbd-i]="6" aria-labelledby="sbd-users-title">
    <div class="sbd-panel__header sbd-panel__header--padded">
      <h3 class="sbd-panel__title" id="sbd-users-title">Usage by user</h3>
      <div class="sbd-users-controls">
        <lib-sandbox-dashboard-dropdown
          prefix="Sort:"
          ariaLabel="Sort users"
          align="right"
          [options]="sortOptions"
          [value]="sort()"
          (valueChange)="setSort($event)"
        ></lib-sandbox-dashboard-dropdown>
        <div class="sbd-search">
          <svg class="sbd-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="search"
            class="sbd-search__input"
            placeholder="Search users"
            maxlength="100"
            aria-label="Search users by email or user ID"
            [value]="searchQuery()"
            (input)="onSearchInput($any($event.target).value)"
            (keydown.enter)="applySearch()"
          />
          @if (searchQuery()) {
            <button type="button" class="sbd-search__clear" aria-label="Clear search" (click)="clearSearch()">×</button>
          }
        </div>
      </div>
    </div>

    @if (errors().users; as message) {
      <div class="sbd-alert sbd-alert--danger sbd-alert--inset" role="alert">
        <span>{{ message }}</span>
        <button type="button" class="sbd-link-btn" (click)="retry('users')">Retry</button>
      </div>
    } @else {
      <div class="sbd-table-wrap" [class.sbd-is-refreshing]="loading().users && users()">
        <table class="sbd-table">
          <thead>
            <tr>
              <th scope="col">User</th>
              @if (showCpu()) { <th scope="col">Running CPU</th> }
              @if (showGpu()) { <th scope="col">Running GPU</th> }
              @if (showCpu()) { <th scope="col">CPU hours</th> }
              @if (showGpu()) { <th scope="col">GPU hours</th> }
            </tr>
          </thead>
          <tbody>
            @if (loading().users && !users()) {
              @for (i of [1, 2, 3]; track i) {
                <tr>
                  <td [attr.colspan]="userColumnCount()"><span class="sbd-skeleton sbd-skeleton--line"></span></td>
                </tr>
              }
            } @else {
              @for (row of userRows(); track row.userId) {
                <tr class="sbd-table__row" [class.sbd-table__row--expanded]="expandedUserId() === row.userId">
                  <td>
                    <button
                      type="button"
                      class="sbd-user-toggle"
                      [attr.aria-expanded]="expandedUserId() === row.userId"
                      [attr.aria-label]="'Show sandboxes for ' + row.label"
                      (click)="toggleUser(row.userId)"
                    >
                      <svg class="sbd-user-toggle__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                      <span class="sbd-user-toggle__label" [title]="row.label">{{ row.label }}</span>
                    </button>
                  </td>
                  @if (showCpu()) { <td>{{ row.runningCpu }}</td> }
                  @if (showGpu()) { <td>{{ row.runningGpu }}</td> }
                  @if (showCpu()) { <td>{{ row.cpuHours }}</td> }
                  @if (showGpu()) { <td>{{ row.gpuHours }}</td> }
                </tr>

                @if (expandedUserId() === row.userId) {
                  <tr class="sbd-drill">
                    <td [attr.colspan]="userColumnCount()">
                      <div class="sbd-drill__body">
                        <p class="sbd-drill__title">Current sandboxes</p>
                        @if (loading().sandboxes) {
                          <span class="sbd-skeleton sbd-skeleton--line"></span>
                        } @else if (errors().sandboxes) {
                          <div class="sbd-drill__empty">
                            {{ errors().sandboxes }}
                            <button type="button" class="sbd-link-btn" (click)="retry('sandboxes')">Retry</button>
                          </div>
                        } @else if (userSandboxes().length === 0) {
                          <p class="sbd-drill__empty">No current sandboxes for this user.</p>
                        } @else {
                          <table class="sbd-subtable">
                            <thead>
                              <tr>
                                <th scope="col">Sandbox</th>
                                <th scope="col">Type</th>
                                <th scope="col">State</th>
                                <th scope="col">CPU</th>
                                <th scope="col">Memory</th>
                                <th scope="col">GPU</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (s of userSandboxes(); track s.id) {
                                <tr>
                                  <td [title]="s.name">{{ s.name }}</td>
                                  <td>{{ s.resourceType }}</td>
                                  <td><span class="sbd-state" [ngClass]="'sbd-state--' + s.stateTone">{{ s.stateLabel }}</span></td>
                                  <td>{{ s.cpu }}</td>
                                  <td>{{ s.memory }}</td>
                                  <td>{{ s.gpu }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        }
                      </div>
                    </td>
                  </tr>
                }
              } @empty {
                <tr>
                  <td class="sbd-table__empty" [attr.colspan]="userColumnCount()">
                    {{ searchQuery() ? 'No users match your search.' : 'No sandbox usage in this period.' }}
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <footer class="sbd-table-footer">
        @if (pagination(); as p) {
          @if (p.totalPages > 1) {
            <nav class="sbd-pager" aria-label="Users pages">
              @if (p.page > 1) {
                <button type="button" class="sbd-pager__btn sbd-pager__btn--nav" [disabled]="loading().users" (click)="goToPage(1)" aria-label="First page">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="11 17 6 12 11 7"></polyline><polyline points="18 17 13 12 18 7"></polyline></svg>
                </button>
                <button type="button" class="sbd-pager__btn sbd-pager__btn--nav" [disabled]="loading().users" (click)="goToPage(p.page - 1)" aria-label="Previous page">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
              }
              @for (item of pageItems(); track item) {
                <button
                  type="button"
                  class="sbd-pager__btn"
                  [class.sbd-pager__btn--active]="item === p.page"
                  [attr.aria-current]="item === p.page ? 'page' : null"
                  [attr.aria-label]="'Page ' + item"
                  [disabled]="loading().users"
                  (click)="goToPage(item)"
                >
                  {{ item }}
                </button>
              }
              @if (p.page < p.totalPages) {
                <button type="button" class="sbd-pager__btn sbd-pager__btn--nav" [disabled]="loading().users" (click)="goToPage(p.page + 1)" aria-label="Next page">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
                <button type="button" class="sbd-pager__btn sbd-pager__btn--nav" [disabled]="loading().users" (click)="goToPage(p.totalPages)" aria-label="Last page">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="13 17 18 12 13 7"></polyline><polyline points="6 17 11 12 6 7"></polyline></svg>
                </button>
              }
            </nav>
          }
        }

        <div class="sbd-table-footer__size">
          <lib-sandbox-dashboard-dropdown
            prefix="Showing:"
            ariaLabel="Results per page"
            align="right"
            [openUp]="true"
            [options]="pageSizeOptions"
            [value]="pageSize()"
            (valueChange)="setPageSize($event)"
          ></lib-sandbox-dashboard-dropdown>
        </div>
      </footer>
    }
    <p class="sbd-footnote">Session hours cover recorded booking sessions. Resource values are requests.</p>
  </section>
</div>
